import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Flex, Table, Thead, Tbody, Tr, Th, Td, Avatar, Button, Text, UnorderedList, ListItem, IconButton } from '@chakra-ui/react';
import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { getColor } from '../utils/colorUtils';
import { Modal, ModalOverlay, ModalContent, ModalBody, useDisclosure } from '@chakra-ui/react';
import PlayerCard from './PlayerCard';
import TradeModal from './TradeModal';
import QuickPurchaseModal, { QuickPurchaseResult } from './QuickPurchaseModal';
import { CURRENT_YEAR, ROSTER_MOVES_CONFIRMATION_MESSAGES, ROSTER_MOVES_CONFIRMATION_BUTTONS } from '../constants';
import { formatSalary } from '../utils/formatMoney';
import { useUserContext } from '../contexts/UserContext';
import { showPlayerNotes } from '../utils/showPlayerNotes';
import { useDeals } from '../hooks/useDeals';
import type { ShopItem } from '../store/shoppingCartStore';
import ConfirmDialog from './ConfirmDialog';
import { useMyLeagueStore } from '../store/myLeagueStore';
import { formatPstDateTime } from '../lib/pstTime';
import WarningPanel from './WarningPanel';
import { formatPositionsList } from '../utils/formatPositionsList';
import { httpsPlayerImageUrl } from '../utils/stringUtils';

interface Player {
  id: number;
  /** Season defensive buckets from roster API (`rosterHelper`); preferred for POS column */
  primaryPosition?: string[];
  secondaryPosition?: string[];
  tertiaryPosition?: string[];
  rwPlayer: {
    id: number;
    first_name: string;
    last_name: string;
    position_1?: string;
    position_2?: string;
    position_3?: string;
    mlb_team: string;
    image?: string;
    bats?: string;
    throws?: string;
    rule_five_eligibility?: string;
    mls?: number;
    options?: string;
    prior_or?: string;
  };
  additionalInfo?: {
    mls?: number;
    options?: number;
    prior_or?: string;
    rule_five_eligibility?: boolean;
    days_active?: number[];
    team_started_season?: number;
  };
  playerContract?: {
    current_salary: number;
    contractYears?: { year: number; salary: number }[];
    options?: number; // Added for option years
  };
  contract?: {
    current_salary: number;
    contractYears?: { year: number; salary: number }[];
  };
  level: string;
  status: string;
  status_until: string | null;
  is_40_man: boolean;
  year: number;
  deal_status?: string | null;
  deal_until?: string | null;
  team_id?: number;
}

interface Team {
  id: number;
  name: string;
  logo?: string;
  color_primary?: string;
  color_secondary?: string;
  color_text?: string;
  salary_cap?: number;
  prefix?: string;
  extra_info?: {
    starting_budget?: string;
  };
  salaryCapDescription?: string | null;
}

interface SectionConfig {
  label: string;
  order: number;
  status: string[];
  level: string;
  actionMenu?: string;
  on40Man?: boolean;
}

interface RosterContentProps {
  tabName: string;
  team?: Team | null;
  leagueTeam?: Team | null;
  sectionPlayers: { [label: string]: Player[] };
  unassignedPlayers: Player[];
  sectionsConfig: SectionConfig[];
  activeRosterLabel: string;
  activeRosterCount: number;
  onBackToTop?: () => void;
  isMyTeam?: boolean;
  totalPayroll?: number;
  activeRoster?: number;
  fortyManRoster?: number;
  refetch?: () => Promise<void>; // Add refetch prop
  rosterMove?: (playerId: number, transactionName: string) => Promise<unknown>; // Add rosterMove prop
  currentTeamId?: number | null; // Add currentTeamId to identify non-roster players
  rosterLocked?: boolean; // Whether the roster is currently locked
}

interface ColumnConfig {
  header: string;
  render: (entry: Player, sectionLabel: string, teamInfo?: Team) => React.ReactNode;
  key: string;
}

const getRosterColumnsConfig = (
  handleDealClick: (dealId: string) => void,
  myTeam?: number | null,
  myLeague?: any
): Record<string, ColumnConfig[]> => ({
  'Roster Management': [
    {
      header: 'Player',
      key: 'player',
      render: (entry: Player, sectionLabel: string, teamInfo?: Team) => (
        <Flex align="center">
          {/* Show solid diamond if on 40-man and NOT in Active Roster, but NOT for Non-roster Players with Carried Salaries */}
          {entry.is_40_man && entry.status !== 'DL60' && sectionLabel !== 'Active Roster' && sectionLabel !== 'Non-roster Players with Carried Salaries' && (
            <Box as="span" className="dominus-blue" fontWeight="bold" fontSize="lg" mr="2">◆</Box>
          )}
          {httpsPlayerImageUrl(entry.rwPlayer.image) ? (
            <Box
              w="40px"
              h="40px"
              borderRadius="full"
              overflow="hidden"
              mr={2}
              bg="gray.600"
              flexShrink={0}
            >
              <img
                src={httpsPlayerImageUrl(entry.rwPlayer.image)}
                alt={`${entry.rwPlayer.first_name} ${entry.rwPlayer.last_name}`}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </Box>
          ) : (
            <Avatar
              size="md"
              name={`${entry.rwPlayer.first_name} ${entry.rwPlayer.last_name}`}
              mr={2}
              flexShrink={0}
              bg={teamInfo?.color_primary || 'gray.600'}
              color={teamInfo?.color_text || 'white'}
              border={`2px solid ${teamInfo?.color_secondary || 'transparent'}`}
            />
          )}
          {entry.rwPlayer.first_name} {entry.rwPlayer.last_name}
        </Flex>
      ),
    },
    {
      header: 'Notes',
      key: 'notes',
      render: (entry: Player) => {
        const notes = showPlayerNotes(entry, handleDealClick, myTeam, myLeague);
        if (notes.length === 0) {
          return '';
        }

        if (entry.deal_status === 'franchise_tag') {
          return (<span>{notes[0]}</span>)
        }

        return (
          <UnorderedList spacing={1} fontSize="xs" color="gray.300">
            {notes.map((note, index) => (
              <ListItem key={index}>
                {typeof note === 'string' ? note : note}
              </ListItem>
            ))}
          </UnorderedList>
        );
      },
    },
    {
      header: 'POS',
      key: 'pos',
      render: (entry) => formatPositionsList(entry),
    },
    {
      header: 'B',
      key: 'bats',
      render: (entry) => entry.rwPlayer.bats || '-',
    },
    {
      header: 'T',
      key: 'throws',
      render: (entry) => entry.rwPlayer.throws || '-',
    },
    {
      header: 'MLS',
      key: 'mls',
      render: (entry) => {
        const mls = entry.additionalInfo?.mls ?? entry.rwPlayer.mls;
        if (typeof mls === 'number' && !isNaN(mls)) {
          return mls.toFixed(3);
        }
        return '-';
      },
    },
    {
      header: 'Salary',
      key: 'salary',
      render: (entry: Player) => formatSalary(entry.playerContract?.current_salary, false),
    },
    {
      header: 'Total Contract',
      key: 'total_contract',
      render: (entry: Player, _sectionLabel: string) => {
        const val = entry.playerContract?.total_value;
        let totalValueNum: number | null = null;
        if (typeof val === 'string') {
          totalValueNum = parseFloat(val);
        } else if (typeof val === 'number') {
          totalValueNum = val;
        }
        if (totalValueNum !== null && !isNaN(totalValueNum)) {
          return `$${totalValueNum.toLocaleString()}`;
        }
        if (entry.playerContract?.salaries && entry.playerContract.salaries.length > 0) {

          const total = entry.playerContract.salaries.reduce((sum: number, s: { salary: number | string | undefined }) => {
            let salaryNum = 0;
            if (typeof s.salary === 'string') {
              salaryNum = parseFloat(s.salary);
            } else if (typeof s.salary === 'number') {
              salaryNum = s.salary;
            }
            return sum + (isNaN(salaryNum) ? 0 : salaryNum);
          }, 0);
          return `$${total.toLocaleString()}`;
        }
        return '-';
      },
    },
  ],
  'Additional Info': [
    {
      header: 'Player',
      key: 'player',
      render: (entry, sectionLabel, teamInfo?: Team) => (
        <Flex align="center">
          {/* Show solid diamond if on 40-man and NOT in Active Roster, but NOT for Non-roster Players with Carried Salaries */}
          {entry.is_40_man && sectionLabel !== 'Active Roster' && sectionLabel !== 'Non-roster Players with Carried Salaries' && (
            <Box as="span" className="dominus-blue" fontWeight="bold" fontSize="lg" mr="2">◆</Box>
          )}
          {httpsPlayerImageUrl(entry.rwPlayer.image) ? (
            <Box
              w="40px"
              h="40px"
              borderRadius="full"
              overflow="hidden"
              mr={2}
              bg="gray.600"
              flexShrink={0}
            >
              <img
                src={httpsPlayerImageUrl(entry.rwPlayer.image)}
                alt={`${entry.rwPlayer.first_name} ${entry.rwPlayer.last_name}`}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </Box>
          ) : (
            <Avatar
              size="md"
              name={`${entry.rwPlayer.first_name} ${entry.rwPlayer.last_name}`}
              mr={2}
              flexShrink={0}
              bg={teamInfo?.color_primary || 'gray.600'}
              color={teamInfo?.color_text || 'white'}
              border={`2px solid ${teamInfo?.color_secondary || 'transparent'}`}
            />
          )}
          {entry.rwPlayer.first_name} {entry.rwPlayer.last_name}
        </Flex>
      ),
    },
    {
      header: 'POS',
      key: 'pos',
      render: (entry) => formatPositionsList(entry),
    },
    {
      header: 'B/T',
      key: 'bt',
      render: (entry) => `${entry.rwPlayer.bats || '-'}${entry.rwPlayer.throws ? '/' + entry.rwPlayer.throws : ''}`,
    },
    {
      header: 'MLS',
      key: 'mls',
      render: (entry) => {
        const mls = entry.additionalInfo?.mls ?? entry.rwPlayer.mls;
        if (typeof mls === 'number' && !isNaN(mls)) {
          return mls.toFixed(3);
        }
        return '-';
      },
    },
    {
      header: 'Options',
      key: 'options',
      render: (entry) => {
        const options = entry.additionalInfo?.options ?? entry.rwPlayer.options;
        if (options === null || options === undefined || options === '' || isNaN(Number(options))) {
          return '-';
        }
        return options;
      },
    },
    {
      header: 'Prior OR',
      key: 'prior_or',
      render: (entry) => {
        const priorOrYes = ((entry.additionalInfo?.prior_or ?? entry.rwPlayer.prior_or) || '').toLowerCase() === 'yes';
        const mlsVal = entry.additionalInfo?.mls ?? entry.rwPlayer.mls;
        const mlsEligible = typeof mlsVal === 'number' && !isNaN(mlsVal) && mlsVal >= 5;
        return (priorOrYes || mlsEligible) ? 'YES' : <Text fontSize="sm" color="gray.400">no</Text>;
      },
    },
    // No 40 Eligible column can be added here if you have logic/field for it
    {
      header: 'Rule 5 Eligible',
      key: 'rule5',
      render: (entry) => {
        const ruleFiveEligible = entry.additionalInfo?.rule_five_eligibility ?? entry.rwPlayer.rule_five_eligibility;
        return ruleFiveEligible ?? '-';
      },
    },
  ],
  'Contract Details': [
    {
      header: 'Player',
      key: 'player',
      render: (entry, sectionLabel, teamInfo?: Team) => (
        <Flex align="center">
          {/* Show solid diamond if on 40-man and NOT in Active Roster, but NOT for Non-roster Players with Carried Salaries */}
          {entry.is_40_man && sectionLabel !== 'Active Roster' && sectionLabel !== 'Non-roster Players with Carried Salaries' && (
            <Box as="span" className="dominus-blue" fontWeight="bold" fontSize="lg" mr="2">◆</Box>
          )}
          {httpsPlayerImageUrl(entry.rwPlayer.image) ? (
            <Box
              w="40px"
              h="40px"
              borderRadius="full"
              overflow="hidden"
              mr={2}
              bg="gray.600"
              flexShrink={0}
            >
              <img
                src={httpsPlayerImageUrl(entry.rwPlayer.image)}
                alt={`${entry.rwPlayer.first_name} ${entry.rwPlayer.last_name}`}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </Box>
          ) : (
            <Avatar
              size="md"
              name={`${entry.rwPlayer.first_name} ${entry.rwPlayer.last_name}`}
              mr={2}
              flexShrink={0}
              bg={teamInfo?.color_primary || 'gray.600'}
              color={teamInfo?.color_text || 'white'}
              border={`2px solid ${teamInfo?.color_secondary || 'transparent'}`}
            />
          )}
          {entry.rwPlayer.first_name} {entry.rwPlayer.last_name}
        </Flex>
      ),
    },
    {
      header: 'MLS',
      key: 'mls',
      render: (entry) => {
        const mls = entry.additionalInfo?.mls ?? entry.rwPlayer.mls;
        if (typeof mls === 'number' && !isNaN(mls)) {
          return mls.toFixed(3);
        }
        return '-';
      },
    },
    {
      header: '2025 Salary/Pre-Arb',
      key: 'salary_2025',
      render: (entry) => {
        // Use playerContract.current_salary for now, formatted as $X.XXXM if >= 1M, else $XXXk
        const val = entry.playerContract?.current_salary;
        let salaryNum: number | null = null;
        if (typeof val === 'string') {
          salaryNum = parseFloat(val);
        } else if (typeof val === 'number') {
          salaryNum = val;
        }
        if (salaryNum !== null && !isNaN(salaryNum)) {
          if (salaryNum >= 1000000) {
            return `$${(salaryNum / 1000000).toFixed(3)}M`;
          } else if (salaryNum >= 1000) {
            return `$${(salaryNum / 1000).toFixed(0)}k`;
          } else {
            return `$${salaryNum}`;
          }
        }
        return '-';
      },
    },
    { header: 'Performance Bonus', key: 'perf_bonus', render: () => '' },
    { header: 'Award Bonuses', key: 'award_bonus', render: () => '' },
    { header: 'Escalators', key: 'escalators', render: () => '' },
    { header: 'Trade Bonus', key: 'trade_bonus', render: () => '' },
    { header: 'Option Notes', key: 'option_notes', render: () => '' },
  ],
  'default': [
    {
      header: 'Player',
      key: 'player',
      render: (entry, sectionLabel, teamInfo?: Team) => (
        <Flex align="center">
          {/* Show empty diamond if on 40-man (including 10-day DL), but NOT for Non-roster Players with Carried Salaries */}
          {entry.is_40_man && sectionLabel !== 'Non-roster Players with Carried Salaries' && (
            <Box as="span" className="dominus-blue" fontWeight="bold" fontSize="lg" mr="2">◇</Box>
          )}
          {httpsPlayerImageUrl(entry.rwPlayer.image) ? (
            <Box
              w="40px"
              h="40px"
              borderRadius="full"
              overflow="hidden"
              mr={2}
              bg="gray.600"
              flexShrink={0}
            >
              <img
                src={httpsPlayerImageUrl(entry.rwPlayer.image)}
                alt={`${entry.rwPlayer.first_name} ${entry.rwPlayer.last_name}`}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </Box>
          ) : (
            <Avatar
              size="md"
              name={`${entry.rwPlayer.first_name} ${entry.rwPlayer.last_name}`}
              mr={2}
              flexShrink={0}
              bg={teamInfo?.color_primary || 'gray.600'}
              color={teamInfo?.color_text || 'white'}
              border={`2px solid ${teamInfo?.color_secondary || 'transparent'}`}
            />
          )}
          {entry.rwPlayer.first_name} {entry.rwPlayer.last_name}
        </Flex>
      ),
    },
    {
      header: 'POS',
      key: 'pos',
      render: (entry) => formatPositionsList(entry),
    },
    {
      header: 'Salary',
      key: 'salary',
      render: (entry) => entry.playerContract?.current_salary ? `$${entry.playerContract.current_salary.toLocaleString()}` : '-',
    },
    {
      header: '',
      key: 'empty',
      render: () => '',
    },
  ],
});

// Helper to format salary or contract type
const formatContractYear = (cy: { salary?: number | string; contract_type?: string } | undefined) => {
  if (!cy) return '-';
  if (cy.salary && Number(cy.salary) !== 0) {
    return `$${Number(cy.salary).toLocaleString()}`;
  }
  if (cy.contract_type) {
    const t = String(cy.contract_type).trim();
    if (t.toLowerCase() === 'carried') return '-';
    return t;
  }
  return '-';
};


const RosterContent: React.FC<RosterContentProps> = ({ tabName, team, leagueTeam, sectionPlayers, unassignedPlayers, sectionsConfig, activeRosterLabel: _activeRosterLabel, activeRosterCount: _activeRosterCount, onBackToTop, isMyTeam, totalPayroll, activeRoster, fortyManRosterSize, hasFranchiseTag, hasQualifyingOffer, refetch, rosterMove, currentTeamId, rosterLocked }) => {
  // my team
  const myLeague = useMyLeagueStore(state => state.myLeague);
  const { myTeam, systemSettings } = useUserContext();
  const { getSalariesForLeague, salaries } = useDeals(myLeague?.id);
  const isMounted = useRef(false);

  useEffect(() => {
    if (myLeague?.id && getSalariesForLeague && !salaries && !isMounted.current) {
      isMounted.current = true;
      getSalariesForLeague().catch(err => {
        console.error('Error fetching salaries:', err);
      });
    }
  }, [myLeague?.id, getSalariesForLeague, salaries]);


  // rosterMove will be passed as a prop from RosterPage

  // Deal modal state
  const [isDealModalOpen, setIsDealModalOpen] = useState(false);
  const [selectedDealId, setSelectedDealId] = useState<string>('22');

  // Deal click handler
  const handleDealClick = async (dealId: string) => {
    try {
      const deal = await getDealById(parseInt(dealId));
      if (deal) {
        setSelectedDealId(dealId);
        setIsDealModalOpen(true);
      } else {
        console.error('Deal not found:', dealId);
      }
    } catch (error) {
      console.error('Error fetching deal:', error);
    }
  };

  const baseColumns = getRosterColumnsConfig(handleDealClick, myTeam, myLeague);

  const [franchiseTagPlayer, setFranchiseTagPlayer] = useState<Player | null>(null);
  const [isFranchiseModalOpen, setIsFranchiseModalOpen] = useState(false);

  const parseCurrency = (value?: string | number | null) => {
    if (value === null || value === undefined) {
      return undefined;
    }
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : undefined;
    }
    if (typeof value === 'string') {
      const numeric = Number(value.replace(/[^0-9.-]/g, ''));
      return Number.isFinite(numeric) ? numeric : undefined;
    }
    return undefined;
  };

  const franchiseTagPrice = useMemo(() => {
    const leagueValue = parseCurrency(myLeague?.qualifying_offer_salary);
    const salaryValue = parseCurrency(salaries?.franchiseTagPrice ?? salaries?.qualifyingOfferSalary);
    const settingsSource =
      systemSettings?.franchise_tag_price ??
      systemSettings?.franchiseTagPrice ??
      systemSettings?.franchiseTagCost ??
      systemSettings?.franchise_tag_cost ??
      systemSettings?.defaultQualifiedOffer;
    const settingsValue = parseCurrency(settingsSource);
    return leagueValue ?? salaryValue ?? settingsValue ?? 0;
  }, [myLeague?.qualifying_offer_salary, salaries?.franchiseTagPrice, salaries?.qualifyingOfferSalary, systemSettings]);

  const franchiseTagItem: ShopItem | null = useMemo(() => {
    if (!franchiseTagPlayer) {
      return null;
    }
    const price = parseCurrency(franchiseTagPrice);
    if (!price || Number.isNaN(price) || price <= 0) {
      return null;
    }

    return {
      id: `franchise-tag-${franchiseTagPlayer.id}`,
      name: 'Franchise Tag',
      description: `Purchase a one-year contract extension for ${franchiseTagPlayer.rwPlayer.first_name} ${franchiseTagPlayer.rwPlayer.last_name}.`,
      price: Math.round(price),
      currency: 'USD',
      category: 'features',
      metadata: {
        playerId: franchiseTagPlayer.id,
        leagueId: myLeague?.id,
        teamId: myTeam ?? undefined,
        transaction: 'franchise_tag',
        playerPosition: franchiseTagPlayer.primaryPosition?.[0] ?? franchiseTagPlayer.rwPlayer.position_1 ?? '',
      },
    };
  }, [franchiseTagPlayer, franchiseTagPrice, myLeague?.id, myTeam]);

  const handleFranchiseTagIntentCreated = (result: QuickPurchaseResult) => {
    if (result.checkoutUrl) {
      window.open(result.checkoutUrl, '_blank', 'noopener,noreferrer');
    }
  };

  // Modify columns for Non-roster Players with Carried Salaries to use carried contract data
  if (currentTeamId) {
    // Update Salary column for both 'default' and 'Roster Management' tabs
    ['default', 'Roster Management'].forEach(tabKey => {
      const salaryColumn = baseColumns[tabKey]?.find(col => col.key === 'salary');
      if (salaryColumn) {
        const originalRender = salaryColumn.render;
        salaryColumn.render = (entry: Player, sectionLabel: string) => {
          if (sectionLabel === 'Non-roster Players with Carried Salaries') {
            const carriedContract = (entry as any).DominusContracts?.find((contract: any) =>
              contract.carried === true && contract.team_id === currentTeamId
            );
            if (carriedContract?.current_salary) {
              return formatSalary(carriedContract.current_salary, false);
            }
          }
          return originalRender ? originalRender(entry, sectionLabel) : formatSalary(entry.playerContract?.current_salary, false);
        };
      }
    });

    // Update Total Contract column for both 'default' and 'Roster Management' tabs
    ['default', 'Roster Management'].forEach(tabKey => {
      const totalContractColumn = baseColumns[tabKey]?.find(col => col.key === 'total_contract');
      if (totalContractColumn) {
        const originalRender = totalContractColumn.render;
        totalContractColumn.render = (entry: Player, sectionLabel: string) => {
          if (sectionLabel === 'Non-roster Players with Carried Salaries') {
            const carriedContract = (entry as any).DominusContracts?.find((contract: any) =>
              contract.carried === true && contract.team_id === currentTeamId
            );
            if (carriedContract) {
              const val = carriedContract.total_value;
              let totalValueNum: number | null = null;
              if (typeof val === 'string') {
                totalValueNum = parseFloat(val);
              } else if (typeof val === 'number') {
                totalValueNum = val;
              }
              if (totalValueNum !== null && !isNaN(totalValueNum) && totalValueNum > 0) {
                return `$${totalValueNum.toLocaleString()}`;
              }
              if (carriedContract.contractYears && carriedContract.contractYears.length > 0) {
                const total = carriedContract.contractYears.reduce((sum: number, y: any) => {
                  const salaryNum = typeof y.salary === 'number' ? y.salary : (typeof y.salary === 'string' ? parseFloat(y.salary) : 0);
                  return sum + (isNaN(salaryNum) ? 0 : salaryNum);
                }, 0);
                if (total > 0) {
                  return `$${total.toLocaleString()}`;
                }
              }
              return '-';
            }
          }
          return originalRender ? originalRender(entry, sectionLabel) : '-';
        };
      }
    });
  }

  // Handle Contract Details configuration
  if (tabName === 'Contract Details') {
    // Parse currentSeason as a number since systemSettings values are strings
    const currentSeason = systemSettings?.currentSeason
      ? parseInt(systemSettings.currentSeason, 10)
      : CURRENT_YEAR;
    baseColumns['Contract Details'] = [
      baseColumns['Contract Details'][0], // Player column
      baseColumns['Contract Details'][1], // MLS column
      ...[0, 1, 2, 3].map(offset => {
        const year = currentSeason + offset;
        return {
          header: `${year}`,
          key: `year_${year}`,
          render: (entry: any, sectionLabel: string) => {
            if (entry.id === 6503) {
              // debugger;
            }
            // For Non-roster Players with Carried Salaries, use the carried contract for currentTeamId
            if (sectionLabel === 'Non-roster Players with Carried Salaries' && currentTeamId) {
              const carriedContract = entry.DominusContracts?.find((contract: any) =>
                contract.carried === true && contract.team_id === currentTeamId
              );
              if (carriedContract?.contractYears) {
                const cy = carriedContract.contractYears.find(
                  (y: any) => Number(y.year) === year
                );
                if (cy) {
                  return formatContractYear(cy);
                }
              }
            }
            const cy = entry.playerContract?.contractYears?.find(
              (y: any) => Number(y.year) === year
            );
            return formatContractYear(cy);
          }
        };
      }),
      {
        header: 'More',
        key: 'more_years',
        render: (entry: any, sectionLabel: string) => {
          // For Non-roster Players with Carried Salaries, use the carried contract for currentTeamId
          let years = entry.playerContract?.contractYears || [];
          if (sectionLabel === 'Non-roster Players with Carried Salaries' && currentTeamId) {
            const carriedContract = entry.DominusContracts?.find((contract: any) =>
              contract.carried === true && contract.team_id === currentTeamId
            );
            if (carriedContract?.contractYears) {
              years = carriedContract.contractYears;
            }
          }
          const moreYears = years.filter((y: any) => Number(y.year) > currentSeason + 3).sort((a: any, b: any) => Number(a.year) - Number(b.year));
          if (!moreYears.length) return '';
          return (
            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {moreYears.map((y: any) => (
                <li key={y.year}>
                  {y.year}: {formatContractYear(y)}
                </li>
              ))}
            </ul>
          );
        }
      }
    ];
  }

  const columns = baseColumns[tabName] || baseColumns['default'];
  const barBg = getColor(leagueTeam?.color_primary || team?.color_primary, '#222');
  const barText = getColor(leagueTeam?.color_text || team?.color_text, '#fff');
  const barBorder = getColor(leagueTeam?.color_secondary || team?.color_secondary, 'transparent');

  // Modal and selected player state
  const [selectedPlayer, setSelectedPlayer] = React.useState<Player | null>(null);
  const [selectedSubListLabel, setSelectedSubListLabel] = React.useState<string | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Get deals hook
  const { getDealById } = useDeals(team?.id);

  // TradeModal state
  const [isTradeModalOpen, setIsTradeModalOpen] = useState(false);
  const [tradePlayer, setTradePlayer] = useState<Player | null>(null);
  // Extend Offer confirmation state
  const [showQOConfirm, setShowQOConfirm] = useState(false);
  const [qoMessages, setQoMessages] = useState<string[]>([]);
  const [selectedQOPlayer, setSelectedQOPlayer] = useState<Player | null>(null);

  // Collapsed sections state - default all sections collapsed except Pending Free Agency and Active Roster
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(() => {
    const defaultCollapsed = new Set<string>();
    // Add all sections except Pending Free Agency and Active Roster to collapsed by default
    sectionsConfig.forEach(section => {
      if (section.label !== 'Pending Free Agency' && section.label !== 'Active Roster') {
        defaultCollapsed.add(section.label);
      }
    });
    // Also add the new sections to collapsed by default
    defaultCollapsed.add('Awaiting Assignment');
    defaultCollapsed.add('Free Agents Under Contract');
    return defaultCollapsed;
  });

  // Toggle section collapse
  const toggleSectionCollapse = (sectionLabel: string) => {
    setCollapsedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionLabel)) {
        newSet.delete(sectionLabel);
      } else {
        newSet.add(sectionLabel);
      }
      return newSet;
    });
  };



  // Row event handlers
  const handleRowClick = (entry: Player, label: string) => {
    setSelectedPlayer(entry);
    setSelectedSubListLabel(label);
    onOpen();
  };

  // Trade handler
  const handleTradeRequest = (player: Player) => {
    setTradePlayer(player);
    onClose(); // Close the PlayerCard modal
    // Add a small delay to ensure PlayerCard closes before TradeModal opens
    setTimeout(() => {
      setIsTradeModalOpen(true); // Open the TradeModal
    }, 150);
  };


  // Add Free Agents Under Contract section to config and data
  const extendedSectionsConfig = [
    ...sectionsConfig,
    {
      order: 1.1, // Insert right after Active Roster (1) and before other sections
      level: 'AAA',
      status: ['Awaiting Assignment'],
      label: 'Awaiting Assignment',
      actionMenu: undefined,
      on40Man: true,
    },
    {
      order: 98,
      level: 'FA',
      status: ['free_agent', 'free_agent_offer'],
      label: 'Free Agents Under Contract',
      actionMenu: undefined,
      on40Man: false,
    },
    {
      order: 99,
      level: '',
      status: ['waived', 'waivers_outright', "waivers_to_fa", "waivers", "claimed"],
      label: 'On Waivers',
      actionMenu: undefined,
      on40Man: false,
    },
    {
      order: 100,
      level: '',
      status: [],
      label: 'Non-roster Players with Carried Salaries',
      actionMenu: undefined,
      on40Man: false,
    },
  ];

  // Add Free Agents Under Contract to sectionPlayers
  const extendedSectionPlayers = {
    ...sectionPlayers,
    ['Awaiting Assignment']: (() => {
      // Only get players from unassigned (players with "Awaiting Assignment" are excluded from other sections)
      // Filter for awaiting assignment players
      const awaitingPlayers = unassignedPlayers.filter((entry: Player) =>
        entry.status === 'Awaiting Assignment' ||
        (entry.deal_status && entry.deal_status.toLowerCase().includes('awaiting_assignment'))
      );

      return awaitingPlayers;
    })(),
    ['Free Agents Under Contract']: unassignedPlayers.filter(
      (entry: Player) => entry.deal_status && entry.deal_status.toLowerCase().includes('free')
    ),
    ['On Waivers']: (() => {
      // Get all players from all sections plus unassigned
      const allPlayers = [
        ...Object.values(sectionPlayers).flat(),
        ...unassignedPlayers
      ];

      // Filter for waiver-related players
      const waiverPlayers = allPlayers.filter((entry: Player) =>
        entry.status === 'waived' ||
        (entry.status && entry.status.toLowerCase().includes('claim')) ||
        (entry.status && entry.status.toLowerCase().includes('waive')) ||
        entry.deal_status === 'waivers_outright' ||
        entry.deal_status === 'waivers_to_fa' ||
        entry.deal_status === 'waivers' ||
        entry.deal_status === 'claimed' ||
        (entry.deal_status && entry.deal_status.toLowerCase().includes('claim')) ||
        (entry.deal_status && entry.deal_status.toLowerCase().includes('waive'))
      );

      return waiverPlayers;
    })(),
    ['Non-roster Players with Carried Salaries']: (() => {
      // Get all players from all sections plus unassigned
      const allPlayers = [
        ...Object.values(sectionPlayers).flat(),
        ...unassignedPlayers
      ];

      // Filter for players who have a carried contract for the current team but are on another team (non-null team_id !== currentTeamId)
      const nonRosterCarriedPlayers = allPlayers.filter((entry: Player) => {
        if (!currentTeamId) return false;

        // Player must have a non-null team_id to be in this list (they're on another team's roster)
        const playerTeamId = entry.team_id;
        if (playerTeamId == null) return false;
        if (playerTeamId === currentTeamId) return false;

        // Check if player has a carried contract for the current team
        const hasCarriedContractForCurrentTeam = (entry as any).DominusContracts?.some((contract: any) =>
          contract.carried === true &&
          contract.team_id === currentTeamId
        );

        return hasCarriedContractForCurrentTeam;
      });

      return nonRosterCarriedPlayers;
    })(),
  };

  const freeAgentsUnderContract = unassignedPlayers.filter(
    (entry: Player) =>
      entry.deal_status && entry.deal_status.toLowerCase().includes('free') &&
      entry.playerContract &&
      entry.playerContract.current_salary &&
      entry.playerContract.current_salary > 0
  );

  return (
    <Box pt={5} mb={8}>
      {/* Summary bar and legend on the same line */}
      {rosterLocked && (
        <WarningPanel type="warning" title="Roster Locked" messages={['Rosters are currently locked. No roster moves can be made.']} />
      )}
      {team && (
        <Flex align="center" justify="space-between" mb={2}>
          <Box fontWeight="bold" fontSize="lg">
            Total Payroll: ${(totalPayroll || 0).toLocaleString()}
            {team.salary_cap !== undefined && (
              <span style={{ marginLeft: 24 }}>
                Salary Cap: ${Number(team.salary_cap).toLocaleString()}M
                {team.salaryCapDescription && (
                  <span> ({team.salaryCapDescription})</span>
                )}
              </span>
            )}
          </Box>
          <Flex align="center">
            <Box as="span" className="dominus-blue" fontWeight="bold" fontSize="lg" verticalAlign="middle" display="inline-block" lineHeight="1">◆</Box>
            <Text fontSize="sm" className="dominus-blue" ml={1} display="inline">
              = 40-Man Roster &nbsp;
              <Text as="span" color={fortyManRosterSize > 40 ? "red.500" : "inherit"}>
                ({fortyManRosterSize}/40)
                {fortyManRosterSize > 40 && (
                  <Text as="span" ml={2} color="red.500">⚠️</Text>
                )}
              </Text>
            </Text>
          </Flex>
        </Flex>
      )}
      {/* Sectioned tables */}
      {extendedSectionsConfig
        .filter(section => {
          const playerCount = extendedSectionPlayers[section.label]?.length || 0;
          return playerCount > 0;
        })
        .sort((a, b) => a.order - b.order)
        .map(section => (
          <Box mb={8} key={section.label}>
            <Flex
              align="center"
              justify="space-between"
              bg={barBg}
              color={barText}
              border={barBorder !== 'transparent' ? '1.5px solid' : undefined}
              borderColor={barBorder}
              px={4}
              py={2}
              borderRadius="md"
              fontWeight="bold"
              fontSize="lg"
              mb={2}
            >
              <Flex align="center" cursor="pointer" onClick={() => toggleSectionCollapse(section.label)}>
                <IconButton
                  aria-label={collapsedSections.has(section.label) ? "Expand section" : "Collapse section"}
                  icon={collapsedSections.has(section.label) ? <ChevronRightIcon boxSize={6} /> : <ChevronDownIcon boxSize={6} />}
                  size="lg"
                  variant="ghost"
                  color={barText}
                  _hover={{ bg: 'rgba(255, 255, 255, 0.1)' }}
                  mr={3}
                  minW="40px"
                  h="40px"
                />
                <span>{section.label} ({extendedSectionPlayers[section.label]?.length || 0} players)</span>
              </Flex>
              {section.label !== 'Active Roster' && section.label !== 'Pending Free Agency' && onBackToTop && (
                <Button
                  size="xs"
                  variant="ghost"
                  color={barText}
                  _hover={{ bg: 'rgba(255, 255, 255, 0.1)' }}
                  onClick={onBackToTop}
                >
                  Back to Top
                </Button>
              )}
            </Flex>
            {(() => {
              const sectionColumns = section.label === 'Pending Free Agency'
                ? (
                  // Remove Salary and Total Contract; add Action column
                  columns
                    .filter(col => col.key !== 'salary' && col.key !== 'total_contract')
                    .map(col => {
                      // Override notes column for Qualified for Offer section
                      if (col.key === 'notes') {
                        return {
                          ...col,
                          render: (entry: Player) => {
                            // If hasFranchiseTag is not false and deal_status is pending_fa_offer, show special notes
                            if (hasFranchiseTag !== false && entry.deal_status === 'pending_fa_offer') {
                              return (
                                <UnorderedList spacing={1} fontSize="xs" color="gray.300">
                                  <ListItem>
                                    <Text fontSize="sm" color="gray.300">Pending Free Agency</Text>
                                  </ListItem>
                                  <ListItem>
                                    <Text fontSize="sm" color="gray.300">Franchise tag no longer available</Text>
                                  </ListItem>
                                </UnorderedList>
                              );
                            }

                            // Show status-based messages first
                            if (entry.status === 'EFQO') {
                              return <Text fontSize="sm" className="dominus-blue">Eligible for Qualifying Offer</Text>;
                            }

                            if (entry.status === 'Pending FA') {
                              return <Text fontSize="sm" className="dominus-blue">Pending Free Agency</Text>;
                            }

                            // If deal_status is "qualifying_offer_made", show qualifying offer made message
                            if (entry.deal_status === 'qualifying_offer_made') {
                              const decisionText = entry.deal_until ? formatPstDateTime(entry.deal_until) : 'unknown date';
                              return (
                                <UnorderedList spacing={1} fontSize="xs" color="gray.300">
                                  <ListItem>
                                    <Text fontSize="sm" color="gray.300">A qualifying offer was made</Text>
                                  </ListItem>
                                  <ListItem>
                                    <Text fontSize="sm" color="gray.300">Offer decision no later than {decisionText}</Text>
                                  </ListItem>
                                </UnorderedList>
                              );
                            }

                            // If deal_status is "qa_offered", show offer extended message
                            if (entry.deal_status === 'qa_offered') {
                              const expiresText = entry.deal_until ? `Expires ${formatPstDateTime(entry.deal_until)}` : 'Expires unknown';
                              return <Text fontSize="sm" className="dominus-blue">Offer extended. {expiresText}</Text>;
                            }

                            // If deal_status doesn't include "qa_", leave notes blank
                            if (!entry.deal_status || !entry.deal_status.includes('qa_')) {
                              return <Text fontSize="sm" color="gray.400">-</Text>;
                            }

                            const notes = showPlayerNotes(entry, handleDealClick, myTeam, myLeague);
                            if (notes.length === 0) {
                              return <Text fontSize="sm" color="blue.300">Eligible for qualifying offer</Text>;
                            }

                            return (
                              <UnorderedList spacing={1} fontSize="xs" color="gray.300">
                                {notes.map((note, index) => (
                                  <ListItem key={index}>
                                    {typeof note === 'string' ? note : note}
                                  </ListItem>
                                ))}
                              </UnorderedList>
                            );
                          }
                        };
                      }
                      return col;
                    })
                    .concat([
                      {
                        header: 'Action',
                        key: 'action_cta',
                        render: (entry: Player) => {
                          // Don't show buttons if player is not on user's team
                          if (entry.team_id !== myTeam) {
                            return <Text fontSize="sm" color="gray.400">-</Text>;
                          }

                          const isPendingFA = entry.deal_status === 'pending_fa_offer';
                          const buttonText = isPendingFA ? 'Buy Franchise Tag' : 'Extend Qualifying Offer';
                          const franchiseDisabled = isPendingFA && (!franchiseTagPrice || Number(franchiseTagPrice) <= 0);
                          // Disable Buy Franchise Tag button if hasFranchiseTag is not false
                          const franchiseTagUnavailable = isPendingFA && hasFranchiseTag !== false;
                          const isDisabled = entry.deal_status === 'qa_offered' || entry.deal_status === 'qualifying_offer_made' || franchiseDisabled || franchiseTagUnavailable;

                          return (
                            <Button
                              size="sm"
                              colorScheme="blue"
                              isDisabled={isDisabled}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isPendingFA) {
                                  setFranchiseTagPlayer(entry);
                                  setIsFranchiseModalOpen(true);
                                  return;
                                }
                                setSelectedQOPlayer(entry);
                                const base = ROSTER_MOVES_CONFIRMATION_MESSAGES['Extend Offer'] || [];
                                // debugger;
                                const msgs = base.map(m => {
                                  let msg = m.replace('<firstName>', entry.rwPlayer.first_name).replace('<lastName>', entry.rwPlayer.last_name);
                                  msg = msg.replace('<salary>', formatSalary(salaries?.qualifyingOfferSalary, true));
                                  return msg;
                                });
                                setQoMessages(msgs);
                                setShowQOConfirm(true);
                              }}
                            >
                              {buttonText}
                            </Button>
                          );
                        },
                      } as ColumnConfig,
                    ])
                )
                : columns;
              return !collapsedSections.has(section.label) ? (
                <>
                  <Table variant="simple" size="sm" bg="whiteAlpha.100" borderRadius="md">
                    <Thead>
                      <Tr>
                        {sectionColumns.map(col => (
                          <Th key={col.key}>{col.header}</Th>
                        ))}
                      </Tr>
                    </Thead>
                    <Tbody>
                      {extendedSectionPlayers[section.label]?.length === 0 ? (
                        <Tr>
                          <Td colSpan={sectionColumns.length} textAlign="center" color="gray.400">(No Players)</Td>
                        </Tr>
                      ) : (
                        extendedSectionPlayers[section.label].map(entry => (
                          <Tr
                            key={entry.id}
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleRowClick(entry, section.label)}
                            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(44, 130, 201, 0.15)')}
                            onMouseLeave={e => (e.currentTarget.style.background = '')}
                          >
                            {sectionColumns.map(col => (
                              <Td key={col.key}>{col.render(entry, section.label, team || leagueTeam)}</Td>
                            ))}
                          </Tr>
                        ))
                      )}
                    </Tbody>
                  </Table>
                </>
              ) : null;
            })()}
          </Box>
        ))}
      {showQOConfirm && selectedQOPlayer && (
        <ConfirmDialog
          isOpen={showQOConfirm}
          onClose={() => setShowQOConfirm(false)}
          onConfirm={async (result: 'Yes' | 'No') => {
            setShowQOConfirm(false);
            if (result === 'Yes' && selectedQOPlayer) {
              try {
                await rosterMove(selectedQOPlayer.id, 'Extend Offer');
                if (refetch) await refetch();
              } catch (err) {
                console.error('Extend Offer failed', err);
              }
            }
          }}
          title={`Extend Qualifying Offer to ${selectedQOPlayer.rwPlayer.last_name}`}
          messages={qoMessages}
          type="confirmation"
          button1={`Yes, ${ROSTER_MOVES_CONFIRMATION_BUTTONS['Extend Offer']?.button1 || 'Extend Offer'}`}
          button2={`Cancel ${ROSTER_MOVES_CONFIRMATION_BUTTONS['Extend Offer']?.button2 || ''}`}
        />
      )}
      {/* Back to Top button */}
      <Flex justify="flex-end" mt={2}>
        <Button size="xs" onClick={onBackToTop}>Back To Top</Button>
      </Flex>
      {/* PlayerCard Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
        <ModalOverlay />
        <ModalContent bg="transparent" boxShadow="none" border="none">
          <ModalBody p={0}>
            {selectedPlayer && (
              <PlayerCard
                player={selectedPlayer}
                team={leagueTeam}
                subListLabel={selectedSubListLabel || ''}
                onClose={onClose}
                isMyTeam={!!isMyTeam}
                refetch={refetch}
                onTradeRequest={handleTradeRequest}
                hasFranchiseTag={hasFranchiseTag}
                rosterLocked={rosterLocked}
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* TradeModal */}
      {tradePlayer && (
        <TradeModal
          isOpen={isTradeModalOpen}
          onClose={() => {
            setIsTradeModalOpen(false);
            setTradePlayer(null);
          }}
          team1={parseInt(myTeam) || undefined}
          team2={tradePlayer.team_id !== parseInt(myTeam) ? tradePlayer.team_id : undefined}
          players1={tradePlayer.team_id === parseInt(myTeam) ? [tradePlayer.id] : []}
          players2={tradePlayer.team_id !== parseInt(myTeam) ? [tradePlayer.id] : []}
          // Pass the actual player data to avoid API calls
          initialPlayers1={tradePlayer.team_id === parseInt(myTeam) ? [tradePlayer] : []}
          initialPlayers2={tradePlayer.team_id !== parseInt(myTeam) ? [tradePlayer] : []}
        />
      )}

      {/* Deal Modal */}
      {isDealModalOpen && (
        <TradeModal
          isOpen={isDealModalOpen}
          onClose={() => {
            setIsDealModalOpen(false);
            setSelectedDealId(null);
          }}
          tradeId={selectedDealId}
        />
      )}
      <QuickPurchaseModal
        isOpen={isFranchiseModalOpen}
        onClose={() => {
          setIsFranchiseModalOpen(false);
          setFranchiseTagPlayer(null);
        }}
        item={franchiseTagItem}
        onPaymentIntentCreated={handleFranchiseTagIntentCreated}
      />
    </Box>
  );
};

export default RosterContent; 